/* eslint-disable max-lines */
import { AgGridAngular } from '@ag-grid-community/angular';
import {
  CellValueChangedEvent,
  ColDef,
  ColGroupDef,
  EditableCallbackParams,
  FillOperationParams,
  GetRowIdFunc,
  GridApi,
  GridOptions,
  GridReadyEvent,
  ITooltipParams,
  ValueFormatterParams,
} from '@ag-grid-community/core';
import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  DestroyRef,
  EventEmitter,
  inject,
  input,
  Input,
  OnDestroy,
  OnInit,
  Output,
  signal,
} from '@angular/core';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { BehaviorSubject, filter, firstValueFrom, map, take } from 'rxjs';

import { extendableOptionsDropdownRenderer } from '@shared/components/ag-raw-options-dropdown-renderer/ag-raw-options-dropdown-renderer';
import {
  ConfirmationActionModalComponent,
  ConfirmationActionModalData,
} from '@shared/components/modals/confirmation-action-modal/confirmation-action-modal.components';
import { ConfirmationModalComponent } from '@shared/components/modals/confirmation-modal/confirmation-modal.component';
import { TableConstants } from '@shared/constants/table.constants';
import { ActivitySubType } from '@shared/services/gql.service';
import { OverlayService } from '@shared/services/overlay.service';
import { GlobalStore } from '@shared/store/global/global.store';
import { MainQuery } from '@shared/store/main/main.query';
import { PurchaseOrdersQuery } from '@shared/store/purchase-orders/purchase-orders.query';
import { getEncodedAttributeName } from '@shared/utils/get-encoded-attribute-name';

import {
  AgInlineHeaderComponent,
  AgInlineHeaderComponentParams,
} from '../../ag-inline-header.component';
import { AgTooltipComponent } from '../../ag-tooltip.component';
import { BeInlineCategoryDropdownOption } from '../../be-inline-category-dropdown/be-inline-category-dropdown.model';
import {
  BeActivitiesAttributesModalParams,
  BeActivitiesAttributesModalRowData,
  BeAttributesTabState,
  BeAttributesTabToSave,
  InAppDocumentState,
} from '../be-activities-attributes-modal.model';
import {
  AttributesDropdownComponent,
  AttributesDropdownComponentParamsModel,
} from './attributes-dropdown/attributes-dropdown.component';
import { ExistingAttributesService } from './services/existing-attributes.service';

const ERROR_TABLE_TOOLTIP = 'Please resolve errors to continue';
const PIVOT_ATTRIBUTE_ROW_ERROR =
  'Attributes used in JE Configuration must be included for all activities.';

@Component({
  imports: [FormsModule, AgGridAngular, CommonModule],
  selector: 'aux-attributes-tab',
  styleUrls: ['attributes-tab.component.scss'],
  templateUrl: 'attributes-tab.component.html',
})
export class AttributesTabComponent implements OnDestroy, OnInit {
  destroyRef = inject(DestroyRef);
  globalStore = inject(GlobalStore);
  mainQuery = inject(MainQuery);
  purchaseOrdersQuery = inject(PurchaseOrdersQuery);

  private existingAttributesService = inject(ExistingAttributesService);
  private overlayService = inject(OverlayService);

  deletedColumns = signal<string[]>([]);
  headerErrorState = signal<Record<string, boolean>>({});
  renamedColumns = signal<string[]>([]);
  selectedVendor = signal('');
  state = signal<Record<string, Record<string, string>>>({});

  purchaseOrders = toSignal(this.purchaseOrdersQuery.selectAll(), {
    initialValue: [],
  });

  atLeastOneColumnHasChanged = computed(() => {
    return !!(this.deletedColumns().length || this.renamedColumns().length);
  });
  atLeastOneRowHasChanged = computed(() => {
    return this.atLeastOneColumnHasChanged() || !!Object.keys(this.state()).length;
  });
  doesHeaderHasError = computed(() => {
    return Object.values(this.headerErrorState()).some((z) => z);
  });
  poOptions = computed(() => {
    const vendorId = this.selectedVendor();

    return this.purchaseOrders().filter((po) => po.organization_id === vendorId);
  });

  showNotesSection = input.required<boolean>();

  addedColumns: string[] = [];
  addNewColumnDisabled = false;
  @Input() categories: BeInlineCategoryDropdownOption[] = [];
  @Output() closeTab = new EventEmitter<void>();
  columns: ColGroupDef[] = [];
  @Input() data: BeActivitiesAttributesModalParams | null = null;
  deletedRows: BeActivitiesAttributesModalRowData[] = [];
  emptyRowMsg = 'Fill out at least 1 attribute to Save';
  filteredRowData: BeActivitiesAttributesModalRowData[] = [];
  gridAPI!: GridApi<BeActivitiesAttributesModalRowData>;
  @Input() gridData$ = new BehaviorSubject<BeActivitiesAttributesModalRowData[]>([]);
  gridOptions: GridOptions = {};
  @Input() hasExternalChanges = false;
  headerErrorTooltip = ERROR_TABLE_TOOLTIP;
  headerNames: Record<string, string> = {};
  @Input() inAppDocumentState: InAppDocumentState = { bucket_keys: [], notes: '' };
  newColumnCounter = 0;
  originalColumns: string[] = [];
  originalHeaders = '{}';
  rowData: BeActivitiesAttributesModalRowData[] = [];
  rowErrorTooltip = PIVOT_ATTRIBUTE_ROW_ERROR;
  @Output() save = new EventEmitter<BeAttributesTabToSave>();
  saveButtonTooltipForNotes = 'Enter required note to save.';
  @Output() toggleTabs = new EventEmitter<BeAttributesTabState>();
  vendors: { id: string; label: string }[] = [];

  private existingAttributesData$ = toObservable(this.existingAttributesService.data);

  ngOnInit(): void {
    this.existingAttributesService.fetchAttributes().catch(console.error);
    this.gridData$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((value) => {
      this.rowData = value;
      this.deletedRows = this.rowData.filter((row) => row.deleted);
      this.filteredRowData = this.rowData
        .filter((row) => !row.deleted && row.activity_name)
        .slice(0);
    });

    if (this.data) {
      this.vendors = [this.data.vendor];
      this.selectedVendor.set(this.data.vendor.id);

      this.columns = [
        {
          children: [
            {
              cellClass: '!block truncate text-left',
              editable: false,
              field: 'category',
              headerClass: 'ag-header-align-center text-sm',
              headerName: 'Category',
              minWidth: 150,
              tooltipValueGetter: (params: ITooltipParams) => this.getCategoryName(params),
              valueFormatter: (params: ValueFormatterParams) => this.getCategoryName(params),
            },
            {
              cellClass: '!block truncate text-left',
              editable: false,
              field: 'activity_name',
              headerClass: 'ag-header-align-center text-sm',
              headerName: 'Activity',
              minWidth: 200,
              tooltipField: 'activity_name',
            },
          ],
          headerClass: 'ag-header-align-center',
          headerName: 'Attributes',
        },
      ];

      const primaryAttribute = this.globalStore.primaryAttribute();
      const secondaryAttribute = this.globalStore.secondaryAttribute();

      this.data.attributes.forEach((attr) =>
        this.parseAttribute(attr, primaryAttribute, secondaryAttribute)
      );

      this.originalHeaders = JSON.stringify(this.headerNames);

      this.isAddNewColumnDisabled();

      this.setGridOptions();
    }
  }

  ngOnDestroy(): void {
    this.existingAttributesService.existingAttributes.set([]);
    this.existingAttributesService.data.set(null);
  }

  addNewColumn(): void {
    const headerName = `NAME${++this.newColumnCounter}`;
    const field = `attributes.${getEncodedAttributeName(headerName)}`;

    this.headerNames[field] = '';
    this.columns[0].children.push(
      this.getAttributeColumn({
        autoFocus: true,
        field,
      })
    );
    this.gridAPI.setGridOption('columnDefs', this.columns);
    this.gridAPI.sizeColumnsToFit();
    this.addedColumns.push(field);
    this.isAddNewColumnDisabled();

    this.gridAPI.forEachNode((n) => {
      if (n.data) {
        const property = field.replace('attributes.', '');

        n.updateData({
          ...n.data,
          attributes: { ...n.data.attributes, [property]: '' },
        });
        this.updateState(n.data.id, field, '');
      }

      this.gridAPI.ensureColumnVisible(field, 'middle');
    });
  }

  checkNotes(): boolean {
    return this.showNotesSection() ? !!this.inAppDocumentState.notes : true;
  }

  // eslint-disable-next-line max-lines-per-function
  private getAttributeColumn = ({
    autoFocus,
    field,
    headerName = '',
    isAttributeUsedInJEConfig,
  }: {
    autoFocus: boolean;
    field: string;
    headerName?: string;
    isAttributeUsedInJEConfig?: {
      attribute_name: string;
      expense_populated: boolean;
    };
  }) => {
    const isAttributesEditable = !!this.data?.isAttributesEditable;
    const isAttrPO = isAttributePO(headerName);
    let isHeaderEditable = isAttributesEditable;

    let isHeaderDeletable = isAttributesEditable;

    if (isAttributeUsedInJEConfig) {
      isHeaderDeletable = false;
    }

    if (isAttrPO) {
      isHeaderEditable = false;
      isHeaderDeletable = false;
    }

    const removeColumnTooltip = getRemoveColumnTooltip({
      attributeName: headerName,
      isAttributesEditable,
      isHeaderDeletable,
    });

    return <ColDef>{
      cellClass: '!block text-left !p-0',
      cellEditor: AttributesDropdownComponent,
      cellEditorParams: (): AttributesDropdownComponentParamsModel => ({
        hideClearButton: false,
        isNotExtendableDropDown: isAttrPO,
        vendorId: this.selectedVendor(),
      }),
      cellRenderer: extendableOptionsDropdownRenderer,
      colId: isAttrPO ? 'PO #' : field,
      editable: (params) =>
        this.isCellEditable({
          isAttributeUsedInJEConfig,
          params,
        }),
      field,
      headerClass: () => {
        const name = this.headerNames[field];
        const { [field]: _, ...rest } = this.headerNames;

        const bool = !name || Object.values(rest).includes(name);

        this.headerErrorState.update((state) => {
          return {
            ...state,
            [field]: bool,
          };
        });

        return ['ag-header-align-center text-sm !p-0', bool ? 'error-header' : ''];
      },
      headerComponent: AgInlineHeaderComponent,
      headerComponentParams: <AgInlineHeaderComponentParams>{
        autoFocus,
        editable: isHeaderEditable,
        field,
        getHeaderNames: () => this.headerNames,
        headerErrorState: this.headerErrorState,
        onBlur: (name: string) => {
          if (this.originalColumns.includes(field) && this.headerNames[field] != name) {
            this.renamedColumns.update((c) => {
              return [...c.filter((f) => f != field), field];
            });
          }

          this.headerNames[field] = name;
          this.gridAPI.setGridOption('columnDefs', [...this.columns]);
        },
        // eslint-disable-next-line max-lines-per-function
        onRemoveColumn: async () => {
          if (!autoFocus) {
            const ref = this.overlayService.openPopup<
              { maxWidth: number; message: string },
              boolean | undefined,
              ConfirmationModalComponent
            >({
              content: ConfirmationModalComponent,
              data: {
                maxWidth: 430,
                message:
                  'Deleting this attribute column will delete the attribute column for the entire vendor budget. Are you sure you want to continue?',
              },
              settings: {
                header: 'Delete Attribute?',
                primaryButton: { label: 'Delete' },
                secondaryButton: { label: 'Go Back' },
              },
            });

            const { data: refData } = await firstValueFrom(ref.afterClosed$);

            if (!refData) {
              return;
            }
          }

          const name = this.headerNames[field];
          const [def] = this.columns;

          def.children = def.children.filter((c) => (c as ColDef).field !== field);
          this.columns = [def];
          this.addedColumns = this.addedColumns.filter((f) => f !== field);

          if (this.originalColumns.includes(field)) {
            this.renamedColumns.update((c) => {
              return c.filter((f) => f != field);
            });
            this.deletedColumns.update((c) => {
              return [...c, field];
            });
          }

          delete this.headerNames[field];
          this.headerErrorState.update((state) => {
            const { [field]: _, ...rest } = state;

            return {
              ...rest,
            };
          });

          const columnsWithTheSameName = Object.keys(this.headerNames).filter(
            (key) => name === this.headerNames[key]
          );

          if (columnsWithTheSameName.length === 1) {
            this.headerErrorState.update((state) => {
              const { [`${columnsWithTheSameName[0]}`]: _, ...rest } = state;

              return {
                ...rest,
              };
            });
          }

          this.gridAPI?.setGridOption('columnDefs', [...this.columns]);
          this.gridAPI.sizeColumnsToFit();
          this.isAddNewColumnDisabled();

          this.gridAPI.forEachNode((n) => {
            const data = n.data;

            if (data) {
              this.state.update((state) => {
                const row = state[data.id] || {};
                const { [field]: _, ...rest } = row;

                if (Object.keys(rest).length) {
                  return {
                    ...state,
                    [data.id]: {
                      ...rest,
                    },
                  };
                } else {
                  const { [data.id]: __, ...restRows } = state;

                  return restRows;
                }
              });
            }
          });
        },
        removable: isHeaderDeletable,
        removeColumnDisabled: !isHeaderDeletable,
        removeColumnTooltip,
      },
      headerName,
      minWidth: 150,
      suppressHeaderKeyboardEvent: () => {
        return true;
      },
      suppressKeyboardEvent: (params) => {
        const isDelete = ['Backspace', 'Delete'].includes(params.event.key);

        if (!isDelete) return false;

        return !this.isCellEditable({ isAttributeUsedInJEConfig, params });
      },
      tooltipField: field,
    };
  };

  private getCategoryName(params: ITooltipParams | ValueFormatterParams): string {
    const categoryName =
      this.categories.find(
        (category) => category.id === params.value && category.type === params.data.activity_type
      )?.name || '';

    switch (params.data.activity_type) {
      case ActivitySubType.ACTIVITY_INVESTIGATOR_PATIENT_INVOICEABLES:
      case ActivitySubType.ACTIVITY_INVESTIGATOR_PATIENT_VISITS:
      case ActivitySubType.ACTIVITY_INVESTIGATOR_SITE_INVOICEABLES:
        return `Investigator > ${categoryName}`;
      default:
        return categoryName;
    }
  }

  private getDeletedAndRenamedColumns() {
    const headers: Record<string, string> = JSON.parse(this.originalHeaders);
    const deletedColumns: string[] = [];
    const renamedColumns: { newValue: string; oldValue: string }[] = [];
    const addedColumns: string[] = [];

    Object.entries(headers).forEach(([key, value]) => {
      if (key in this.headerNames) {
        if (value !== this.headerNames[key]) {
          renamedColumns.push({ newValue: this.headerNames[key], oldValue: value });
        }
      } else {
        deletedColumns.push(value);
      }
    });

    this.gridAPI.getAllGridColumns().forEach((column) => {
      const colId = column.getColId();

      if (colId.startsWith('attributes.')) {
        if (!headers[colId]) {
          addedColumns.push(this.headerNames[colId]);
        }
      }
    });

    return {
      addedColumns,
      deletedColumns,
      renamedColumns,
    };
  }

  private getRowData(): BeActivitiesAttributesModalRowData[] {
    const state = this.state();

    return this.rowData.map((row) => {
      let attributes = row.attributes;
      const obj: Record<string, string> = {};

      Object.entries(this.headerNames).forEach(([key, value]) => {
        obj[value] =
          state[row.id]?.[key] != null
            ? state[row.id][key]
            : attributes[key.replace('attributes.', '')] || '';
      });
      attributes = obj;

      return {
        ...row,
        attributes,
        changed: row.changed || !!state[row.id],
      };
    });
  }

  getRowId: GetRowIdFunc<BeActivitiesAttributesModalRowData> = (d) => {
    return d.data.id;
  };

  goToActivitiesTab(): void {
    if (!this.gridAPI) {
      return;
    }

    this.gridAPI.selectAll();
    this.gridData$.next([...this.gridAPI.getSelectedRows(), ...this.deletedRows]);
    this.toggleTabs.emit({
      ...this.getDeletedAndRenamedColumns(),
      attributesTabHasUnsavedChanges: this.atLeastOneRowHasChanged() && !this.doesHeaderHasError(),
      headerNames: this.headerNames,
      state: this.state(),
    });
    this.gridAPI.deselectAll();
  }

  ifAnyRowHasError() {
    const primary = this.globalStore.primaryAttribute()?.attribute_name;
    const secondary = this.globalStore.secondaryAttribute()?.attribute_name;

    if (!primary) {
      return false;
    }

    const primaryField = `${getEncodedAttributeName(primary)}`;
    const secondaryField = secondary ? `${getEncodedAttributeName(secondary)}` : '';

    const rows = this.filteredRowData;

    for (const row of rows) {
      if (!row.attributes[primaryField]) {
        return true;
      }

      if (secondaryField && !row.attributes[secondaryField]) {
        return true;
      }
    }

    return false;
  }

  private isAddNewColumnDisabled(): void {
    const MAX_COLUMNS_LENGTH = 15; // Category + Activity + PO # + Custom Attributes

    this.addNewColumnDisabled =
      this.columns[0].children.length >= MAX_COLUMNS_LENGTH || !this.data?.isAttributesEditable;
  }

  isAttributesTabHasChanges(): boolean {
    return !!(
      this.atLeastOneRowHasChanged() ||
      this.addedColumns.length ||
      this.renamedColumns.length ||
      this.deletedColumns.length
    );
  }

  isCellEditable({
    isAttributeUsedInJEConfig,
    params,
  }: {
    isAttributeUsedInJEConfig: undefined | { attribute_name: string; expense_populated: boolean };
    params: EditableCallbackParams<BeActivitiesAttributesModalRowData>;
  }): boolean {
    // if the attribute is not used in JE Config, it is editable
    if (!isAttributeUsedInJEConfig) {
      return true;
    }

    return !params.data?.actualsToDateExists;
  }

  isSaveBtnDisabled(): boolean {
    if (!this.checkNotes()) {
      return true;
    }

    if (!this.hasExternalChanges && !this.isAttributesTabHasChanges()) {
      return true;
    }

    if (this.doesHeaderHasError()) {
      return true;
    }

    if (this.ifAnyRowHasError()) {
      return true;
    }

    return false;
  }

  onCellChanged(event: CellValueChangedEvent<BeActivitiesAttributesModalRowData>): void {
    const rowData = event.data;
    const colID = event.column.getColId();

    this.updateState(rowData.id, colID, event.newValue || '');
  }

  onClose(): void {
    this.closeTab.emit();
  }

  onGridReady(event: GridReadyEvent<BeActivitiesAttributesModalRowData>): void {
    this.gridAPI = event.api;
    this.gridAPI.sizeColumnsToFit();

    this.existingAttributesData$
      .pipe(
        filter(Boolean),
        take(1),
        map(() => this.gridAPI),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(this.existingAttributesService.initAttributes);
  }

  async onSave() {
    const emitSave = () => {
      this.save.emit({
        ...this.getDeletedAndRenamedColumns(),
        notes: this.inAppDocumentState.notes,
        rows: this.getRowData(),
        supporting_document_s3_bucket_keys: this.inAppDocumentState.bucket_keys,
      });
    };

    if (this.addedColumns.length || this.atLeastOneColumnHasChanged()) {
      const resp = await this.showChangeAttributesConfirmation();

      if (resp) {
        emitSave();
      }

      return;
    }

    emitSave();
  }

  parseAttribute(
    attr: { attribute_name: string; attribute_value: string },
    primaryAttribute: { attribute_name: string; expense_populated: boolean },
    secondaryAttribute: { attribute_name: string; expense_populated: boolean }
  ) {
    const field = `attributes.${getEncodedAttributeName(attr.attribute_name)}`;

    const isAttributeUsedInJEConfig = isAttributeUsedInJEConfigCheck(attr.attribute_name, [
      primaryAttribute,
      secondaryAttribute,
    ]);

    this.columns[0].children.push(
      this.getAttributeColumn({
        autoFocus: false,
        field,
        headerName: attr.attribute_name,
        isAttributeUsedInJEConfig,
      })
    );
    this.headerNames[field] = attr.attribute_name;
    this.originalColumns.push(field);
  }

  saveBtnTooltip(): string {
    if (this.doesHeaderHasError()) {
      return this.headerErrorTooltip;
    }

    if (!this.hasExternalChanges && !this.isAttributesTabHasChanges()) {
      return this.emptyRowMsg;
    }

    if (!this.checkNotes()) {
      return this.saveButtonTooltipForNotes;
    }

    if (this.ifAnyRowHasError()) {
      return PIVOT_ATTRIBUTE_ROW_ERROR;
    }

    return '';
  }

  scrollToNotes() {
    if (
      !this.checkNotes() &&
      !this.doesHeaderHasError() &&
      (this.hasExternalChanges || this.isAttributesTabHasChanges())
    ) {
      const elem = document.getElementById('notes');
      const auxInputElemId = elem?.children[0].id || '';
      const auxInputElem = document.getElementById(auxInputElemId);

      if (auxInputElem) {
        auxInputElem.focus();
        auxInputElem.blur();
      }
    }
  }

  setGridOptions(): void {
    this.gridOptions = <GridOptions>{
      ...TableConstants.DEFAULT_GRID_OPTIONS.EDIT_GRID_OPTIONS,
      columnDefs: this.columns,
      defaultColDef: {
        editable: true,
        suppressMenu: true,
        suppressMovable: true,
        tooltipComponent: AgTooltipComponent,
        tooltipComponentParams: {
          notShowTooltipForEditMode: true,
        },
      },
      enableFillHandle: true,
      enableRangeSelection: true,
      fillOperation: (params: FillOperationParams) => {
        const newValue = params.values[params.values.length - 1];

        if (params.column.getColId() === 'PO #') {
          if (!newValue) {
            return '';
          }

          if (!this.poOptions().some((po) => po.po_number === newValue)) {
            return params.currentCellValue;
          }
        }

        return newValue;
      },
      headerHeight: 32,
      onBodyScroll: (event) => {
        const dropdown = event.api.getCellEditorInstances()[0];

        if (dropdown && dropdown instanceof AttributesDropdownComponent) {
          dropdown.close();
        }
      },
      processCellFromClipboard: (params) => {
        if (params.column.getColId() === 'PO #') {
          if (!params.value) {
            return '';
          }

          if (!this.poOptions().find((po) => po.po_number === params.value)) {
            return params.node!.data.attributes[getEncodedAttributeName('PO #')];
          }
        }

        return params.value;
      },
      rowClassRules: {},
      stopEditingWhenCellsLoseFocus: false,
      tooltipShowDelay: 0,
    };
  }

  private showChangeAttributesConfirmation = async () => {
    const result = this.overlayService.openPopup<
      ConfirmationActionModalData,
      boolean,
      ConfirmationActionModalComponent
    >({
      data: {
        hideConfirmationHint: true,
        message:
          'Updates to attribute columns will apply to every vendor budget.<div class="mt-4">Are you sure you want to proceed?</div>',
        skipKeywordConfirmation: true,
      },
      modal: ConfirmationActionModalComponent,
      settings: {
        header: 'Update Attributes Confirmation',
        primaryButton: {
          label: 'Confirm',
        },
      },
    });

    const event = await result.afterClosed$.toPromise();

    return !!event?.data;
  };

  private updateState(id: string, colID: string, value: ''): void {
    this.state.update((state) => {
      const row = state[id] || {};

      return {
        ...state,
        [id]: {
          ...row,
          [colID]: value,
        },
      };
    });
  }
}

const isAttributePO = (attribute: string) => attribute === 'PO #';
const isAttributeUsedInJEConfigCheck = (
  attribute: string,
  jeConfig: {
    attribute_name: string;
    expense_populated: boolean;
  }[]
) => jeConfig.find((config) => config.attribute_name === attribute);
const getRemoveColumnTooltip = ({
  attributeName,
  isAttributesEditable,
  isHeaderDeletable,
}: {
  attributeName: string;
  isAttributesEditable: boolean;
  isHeaderDeletable: boolean;
}) => {
  if (!isAttributesEditable) {
    return 'Functionality not supported in Change Order';
  }

  if (isAttributePO(attributeName)) {
    return 'PO # column is required in every vendor budget.';
  }

  if (!isHeaderDeletable) {
    return 'Cannot delete attribute(s) used in JE Configuration.';
  }

  return '';
};
